import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ExamResultsTable from '../components/ExamResultsTable';
import { useSessionResults } from '../hooks/useSession';
import { useCertification } from '../hooks/useCertification';
import { useCertStore } from '../stores/certStore';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { exportQuestionsPdf } from '../utils/pdfExport';
import { downloadCertificate } from '../api/sessions';
import { Button, Card, LinkButton, AppShell } from '../components/ui';

const DEFAULT_PASSING_SCORE = 70;

export default function ExamResults() {
  const { t } = useTranslation();
  useDocumentTitle(t('study.examResults.documentTitle'));
  const { sessionId } = useParams<{ sessionId: string }>();
  const parsedId = sessionId ? Number(sessionId) : null;
  const { data: results, isLoading, error } = useSessionResults(parsedId);
  const { data: certData } = useCertification();
  const certName = useCertStore((s) => s.certName);
  const [showOnlyIncorrect, setShowOnlyIncorrect] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const passingScore = certData?.passing_score_percent ?? DEFAULT_PASSING_SCORE;
  const isPassing = results ? results.score_percent >= passingScore : false;

  const handleDownloadCertificate = async () => {
    if (!parsedId) return;
    setDownloading(true);
    try {
      const blob = await downloadCertificate(parsedId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate_${parsedId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Could show a toast, but keeping it simple
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AppShell maxWidth="7xl" mainClassName="!max-w-4xl">
      <h1 className="mb-8 text-3xl font-extrabold tracking-[-0.02em] text-slate-900 dark:text-white">
        {certName ? t('study.examResults.titleWithCert', { certName }) : t('study.examResults.title')}
      </h1>

      {/* ── Loading ─────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent" />
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────── */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400">
          {t('study.examResults.errorLoad')}
        </div>
      )}

      {results && (
        <>
          {/* ── Score hero ──────────────────────────────────────────── */}
          <Card
            className={`mb-8 p-12 text-center ${
              isPassing
                ? 'ring-2 ring-green-500/30 dark:ring-green-400/30'
                : 'ring-2 ring-red-500/30 dark:ring-red-400/30'
            }`}
          >
            <div className="mb-4">
              <span className="text-6xl" aria-hidden="true">
                {isPassing ? '🎉' : '📚'}
              </span>
            </div>

            {/* Pass/Fail badge */}
            <span
              className={`inline-block rounded-full px-4 py-1 text-sm font-bold uppercase tracking-wider ${
                isPassing
                  ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
                  : 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300'
              }`}
            >
              {isPassing ? t('study.examResults.passed') : t('study.examResults.failed')}
            </span>

            <div className="mt-4 space-y-1">
              <p className="font-mono text-5xl font-extrabold tabular-nums text-blue-600 dark:text-blue-400">
                {results.score_percent}%
              </p>
              <p className="text-lg text-slate-500 dark:text-slate-400">
                {t('study.examResults.correctOutOf', { correct: results.correct, total: results.total })}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('study.examResults.passingScore', { percent: passingScore })}
              </p>
            </div>

            {/* Score bar */}
            <div className="mx-auto mt-6 max-w-md">
              <div className="h-4 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/5">
                <div
                  className={`h-full rounded-full transition-all duration-1000 ${
                    isPassing
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600'
                      : 'bg-gradient-to-r from-red-500 to-rose-500'
                  }`}
                  style={{ width: `${results.score_percent}%` }}
                />
              </div>
              {/* Threshold marker */}
              <div className="relative">
                <div
                  className="absolute -top-4 h-4 w-0.5 bg-slate-900 dark:bg-white"
                  style={{ left: `${passingScore}%` }}
                  aria-hidden="true"
                />
                <span
                  className="absolute -top-0 text-xs text-slate-500 dark:text-slate-400"
                  style={{ left: `${passingScore}%`, transform: 'translateX(-50%)' }}
                >
                  {passingScore}%
                </span>
              </div>
            </div>
          </Card>

          {/* ── Filter toggle ───────────────────────────────────────── */}
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('study.examResults.answerReview')}
            </h2>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <input
                type="checkbox"
                checked={showOnlyIncorrect}
                onChange={(e) => setShowOnlyIncorrect(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 bg-white text-blue-600 focus:ring-blue-500 dark:border-white/20 dark:bg-slate-900/80"
              />
              {t('study.examResults.showOnlyIncorrect')}
            </label>
          </div>

          {/* ── Results table ────────────────────────────────────────── */}
          <Card className="overflow-hidden">
            <ExamResultsTable
              results={results}
              showOnlyIncorrect={showOnlyIncorrect}
            />
          </Card>

          {/* ── Action buttons ───────────────────────────────────────── */}
          <div className="no-print mt-8 flex flex-wrap items-center justify-center gap-4">
            {isPassing && (
              <Button
                type="button"
                onClick={handleDownloadCertificate}
                disabled={downloading}
                variant="primary"
                className="bg-green-600 shadow-[0_6px_16px_-6px_rgba(22,163,74,0.6)] hover:bg-green-700 focus-visible:ring-green-500"
              >
                {downloading ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('study.examResults.downloading')}
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75l3 3m0 0l3-3m-3 3v-7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('study.examResults.downloadCertificate')}
                  </>
                )}
              </Button>
            )}
            <LinkButton to="/exam" variant="primary">
              {t('study.examResults.newExam')}
            </LinkButton>
            <LinkButton to="/study" variant="ghost">
              <span aria-hidden="true">📖</span> {t('study.examResults.studyMode')}
            </LinkButton>
            <LinkButton to="/dashboard" variant="ghost">
              <span aria-hidden="true">🏠</span> {t('study.examResults.dashboard')}
            </LinkButton>
            <Button type="button" variant="ghost" onClick={() => window.print()}>
              <span aria-hidden="true">🖨️</span> {t('study.examResults.print')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (!results) return;
                const pdfQ = results.answers.map((a, i) => ({
                  number: i + 1,
                  text: a.question_text,
                  options: [],
                  correct_answer: t('study.examResults.pdfCorrectPrefix', { correct: a.correct_answer, given: a.given_answer }),
                  explanation: a.explanation,
                }));
                exportQuestionsPdf(pdfQ, `Exam_Results_${sessionId}`);
              }}
            >
              <span aria-hidden="true">📄</span> {t('study.examResults.exportPdf')}
            </Button>
          </div>
        </>
      )}
    </AppShell>
  );
}
